import React, { useCallback, useEffect, useState } from "react";
import { IconButton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import { useHistory } from "react-router";

type ScrollDirection = "left" | "right";
interface Props {
  title: JSX.Element | string;
  /** unique id used to save scroll position. pos is restored when back button is used. */
  uid: string;
  hideScrollButtons?: boolean;
}

const scrollPosHistory: Record<string, number | undefined | null> = {};
export const Scroller: React.FC<Props> = ({ title, children, uid, hideScrollButtons }) => {
  const classes = useStyles();
  const [scrollElm, setScrollElm] = useState<null | HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (history.action !== "POP" || !scrollElm || !scrollPosHistory[uid]) return;
    scrollElm.scrollLeft = scrollPosHistory[uid] || scrollElm.scrollLeft;
    scrollPosHistory[uid] = null;
  }, [scrollElm, history.action, uid]);

  /** Determine if we can scroll left or right more. */
  const calculateCanScroll = useCallback(() => {
    if (!scrollElm) return;
    setCanScrollLeft(scrollElm.scrollLeft !== 0);
    setCanScrollRight(!(scrollElm.scrollWidth - scrollElm.scrollLeft <= scrollElm.clientWidth));
    setCanScroll(scrollElm.scrollWidth > scrollElm.clientWidth);
    scrollPosHistory[uid] = scrollElm.scrollLeft;
  }, [scrollElm, uid]);

  const refCallback = useCallback((node: HTMLDivElement | null) => setScrollElm(node), []);

  // when scroller mounts and ref callback is fired, run onScroll to determine canScrollLeft/Right
  // also handle window resize
  useEffect(() => {
    calculateCanScroll();
    window.addEventListener("resize", calculateCanScroll);
    return () => {
      window.removeEventListener("resize", calculateCanScroll);
    };
  }, [scrollElm, calculateCanScroll]);

  const onScrollDirectionClicked = (direction: ScrollDirection) => {
    if (!scrollElm) return;
    const nextScrollLeft = getNextScrollLeft(scrollElm, direction);
    scrollElm.scrollTo({
      behavior: "smooth",
      left: nextScrollLeft,
    });
  };

  if (typeof hideScrollButtons === "undefined") {
    hideScrollButtons = false;
  }

  return (
    <div className={classes.root_Scroller}>
      <Typography variant="h6" className={classes.title}>
        {title}
      </Typography>

      {/* Scroll Left / Right buttons */}
      {!hideScrollButtons && canScroll && (
        <div className={classes.actions}>
          <IconButton
            disabled={!canScrollLeft}
            className={classes.actionBtn}
            onClick={() => onScrollDirectionClicked("left")}
            children={<ArrowLeft />}
            size="large"
          />
          <IconButton
            disabled={!canScrollRight}
            className={classes.actionBtn}
            onClick={() => onScrollDirectionClicked("right")}
            children={<ArrowRight />}
            size="large"
          />
        </div>
      )}

      <div className={classes.scrollerWrapper}>
        {/* Scrollable element, can go off screen horizontally. */}
        <div
          ref={refCallback}
          onScroll={() => calculateCanScroll()}
          className={classes.scrollableElement}
          children={children}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root_Scroller: {
    paddingBottom: theme.spacing(5),
    position: "relative",
  },
  actions: {
    position: "absolute",
    right: 1,
    top: 0,
  },
  actionBtn: { padding: 0 },
  scrollerWrapper: {
    display: "block",
    scrollbarWidth: "none",
    webkitOverflowScrolling: "touch",
    maxWidth: "100vw",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "91vw",
    },
  },
  scrollableElement: {
    display: "flex",
    overflowX: "scroll",
    overflowY: "hidden",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      width: 0,
      height: 0,
    },
    // "&:hover": {
    //   "&::-webkit-scrollbar": {
    //     width: "inherit",
    //     // height: 20,
    //   },
    // },
  },
  title: { fontWeight: "bold", fontSize: "0.9rem" },
}));

const getNextScrollLeft = (scrollElm: HTMLDivElement, direction: ScrollDirection) => {
  if (direction === "right") {
    return scrollElm.clientWidth - scrollElm.clientWidth * 0.15 + scrollElm.scrollLeft;
  }
  return scrollElm.scrollLeft - (scrollElm.clientWidth - scrollElm.clientWidth * 0.15);
};
